import React, { useState } from 'react';
import useLegoQuery from '../data-caches/useLegoQuery';
import { IconButton } from '../../common/IconButton';
import { SwitchInput } from '../../common/SwitchInput';
import LegoContextSummary from '../LegoContextSummary';
import FuseboxBoxComparison from './FuseboxBoxComparison';

function boxLabel(box, index) {
  return box?.boxName ? `Box ${index + 1}: ${box.boxName}` : `Box ${index + 1}`;
}

function BoxIndexSelect({boxes, value, onChange, id}) {
  return <select className={'form-control form-control-sm d-inline-block w-auto ml-1 mr-1'}
                 id={id}
                 value={value}
                 onChange={e => onChange(Number(e.target.value))}>
    {boxes.map((box, i) => <option key={i} value={i}>{boxLabel(box, i)}</option>)}
  </select>;
}

export default function FuseboxComparison({fuseboxIdA, fuseboxIdB, onMergeFuseboxLegos}) {
  let [idA, setIdA] = useState(fuseboxIdA);
  let [idB, setIdB] = useState(fuseboxIdB);
  let [boxIndexA, setBoxIndexA] = useState(0);
  let [boxIndexB, setBoxIndexB] = useState(0);
  let [normalizeStrings, setNormalize] = useState(false);

  let [a] = useLegoQuery({ _id: idA }, []);
  let [b] = useLegoQuery({ _id: idB }, []);

  const swapFuseboxes = () => {
    setIdA(idB);
    setIdB(idA);
  };

  const swapBoxes = () => {
    setBoxIndexA(boxIndexB);
    setBoxIndexB(boxIndexA);
  };

  let comparison;

  if (a && b) {
    const safeBoxIndexA = Math.min(boxIndexA, Math.max(a.data.length - 1, 0));
    const safeBoxIndexB = Math.min(boxIndexB, Math.max(b.data.length - 1, 0));
    const boxA = a.data[safeBoxIndexA];
    const boxB = b.data[safeBoxIndexB];
    const maxBoxIndex = Math.max(a.data.length, b.data.length) - 1;
    const indexPairs = Array.from({length: maxBoxIndex + 1}, (_, i) => i)
      .filter(i => !(i === safeBoxIndexA && i === safeBoxIndexB));

    comparison = <div>
      <div className={'d-flex justify-content-around flex-wrap'}>
        <div className={'d-flex justify-content-around align-items-center mb-2'}>
          <SwitchInput value={normalizeStrings} onChange={v => setNormalize(v)}>Ignore accents and case</SwitchInput>
        </div>

        <div className={'rounded border p-1 mb-2'}>
          <del>{idA}: </del><LegoContextSummary context={a.context[0]}/>
          <IconButton icon={'merge'} level={'primary'} onClick={() => onMergeFuseboxLegos(idB, idA)}>
            Merge both and keep <del>the red version</del>
          </IconButton>
        </div>

        <div className={'d-flex align-items-center mb-2'}>
          <IconButton icon={'swap_horiz'} level={'secondary'} onClick={swapFuseboxes} description={'Swap fuseboxes'} />
        </div>

        <div className={'rounded border p-1 mb-2'}>
          <ins>{idB}: </ins><LegoContextSummary context={b.context[0]}/>
          <IconButton icon={'merge'} level={'primary'} onClick={() => onMergeFuseboxLegos(idA, idB)}>
            Merge both and keep <ins>the green version</ins>
          </IconButton>
        </div>
      </div>

      <hr/>

      <div className={'d-flex align-items-center flex-wrap mb-2'}>
        Compare
        <BoxIndexSelect boxes={a.data} value={safeBoxIndexA} onChange={setBoxIndexA} id={'fusebox-compare-box-a'}/>
        against
        <BoxIndexSelect boxes={b.data} value={safeBoxIndexB} onChange={setBoxIndexB} id={'fusebox-compare-box-b'}/>
        <IconButton icon={'swap_horiz'} level={'secondary'} className={'ml-2'} onClick={swapBoxes} description={'Swap boxes'} />
      </div>

      <FuseboxBoxComparison boxA={boxA}
                            boxB={boxB}
                            labelA={boxLabel(boxA, safeBoxIndexA)}
                            labelB={boxLabel(boxB, safeBoxIndexB)}
                            normalizeStrings={normalizeStrings}/>

      {maxBoxIndex > 0 && indexPairs.length ?
        <div className={'mt-3'}>
          <div className={'h6'}>All box pairs (by index)</div>
          {indexPairs.map(i => {
            let pairBoxA = a.data[i];
            let pairBoxB = b.data[i];

            if (!pairBoxA && !pairBoxB) {
              return null;
            }

            return <div key={i}>
              <hr/>
              {!pairBoxA || !pairBoxB ?
                <div className={'alert alert-danger'}>Missing box {i + 1}</div> :
                <FuseboxBoxComparison boxA={pairBoxA}
                                      boxB={pairBoxB}
                                      labelA={boxLabel(pairBoxA, i)}
                                      labelB={boxLabel(pairBoxB, i)}
                                      normalizeStrings={normalizeStrings}/>
              }
            </div>;
          })}
        </div> : null}
    </div>;
  } else {
    comparison = <div className={'full-screen-center'}>Loading fuseboxes #{idA} and #{idB}</div>;
  }

  return <div className={'bg-light p-2'}>
    {comparison}
  </div>;
}
